'use client'

import type { ComponentType } from 'react'
import { Bell, BriefcaseBusiness, FileStack, FileText, Home, LayoutDashboard, ListChecks, Plus, Scale, Settings, Users } from 'lucide-react'
import { APP_NAME } from '../lib/branding'

export interface NavItem {
  id: string
  label: string
  icon: ComponentType<{ className?: string }>
  to?: string
  disabled?: boolean
  badge?: string
}

export const navItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', to: '/', icon: LayoutDashboard },
  { id: 'transactions-deals', label: 'Deals', to: '/deals', icon: BriefcaseBusiness },
  { id: 'transactions-new', label: 'New Deal', to: '/deals/new', icon: Plus },
  { id: 'tasks', label: 'Tasks', to: '/tasks', icon: ListChecks },
  { id: 'notifications', label: 'Notifications', to: '/notifications', icon: Bell },
  { id: 'documents', label: 'Documents', to: '/documents', icon: FileText },
  { id: 'layouts', label: 'Layouts', to: '/admin/document-layouts', icon: FileStack },
  { id: 'compliance', label: 'Compliance', to: '/compliance', icon: Scale },
  { id: 'agents', label: 'Agents', to: '/agents', icon: Users },
  { id: 'settings', label: 'Settings', icon: Settings, disabled: true },
]

export const commandItems: NavItem[] = [
  { id: 'command-dashboard', label: 'Dashboard', to: '/', icon: LayoutDashboard },
  { id: 'command-deals', label: 'Deals', to: '/deals', icon: BriefcaseBusiness },
  { id: 'command-new-deal', label: 'New Deal', to: '/deals/new', icon: Plus },
  { id: 'command-tasks', label: 'Tasks', to: '/tasks', icon: ListChecks },
  { id: 'command-notifications', label: 'Notifications', to: '/notifications', icon: Bell },
  { id: 'command-documents', label: 'Documents', to: '/documents', icon: FileText },
  { id: 'command-compliance', label: 'Compliance', to: '/compliance', icon: Scale },
  { id: 'command-agents', label: 'Agents', to: '/agents', icon: Users },
]

export function isCurrentPath(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}

export function isCurrentNavItem(pathname: string, item: NavItem): boolean {
  if (item.to && isCurrentPath(pathname, item.to)) return true
  return false
}

export function getRouteTitle(pathname: string): string {
  if (pathname === '/') return 'Dashboard'
  if (pathname === '/deals') return 'Deals'
  if (pathname === '/deals/new') return 'New Deal'
  if (pathname.startsWith('/deals/')) return 'Deal File'
  if (pathname === '/tasks') return 'Tasks'
  if (pathname === '/notifications') return 'Notifications'
  if (pathname.startsWith('/documents')) return 'Documents'
  if (pathname.startsWith('/admin/document-layouts')) return 'Document Layouts'
  if (pathname === '/compliance') return 'Compliance'
  if (pathname === '/agents') return 'Agents'
  if (pathname.startsWith('/settings')) return 'Settings'
  return APP_NAME
}

export function getBreadcrumbItems(pathname: string): { icon?: React.ReactNode; label: string; to?: string }[] {
  const dashboard = { icon: <Home className="size-4" />, label: 'Dashboard', to: '/' }

  if (pathname === '/') return [{ icon: dashboard.icon, label: dashboard.label }]
  if (pathname === '/deals') return [dashboard, { icon: <BriefcaseBusiness className="size-4" />, label: 'Deals' }]
  if (pathname === '/deals/new') {
    return [
      dashboard,
      { icon: <BriefcaseBusiness className="size-4" />, label: 'Deals', to: '/deals' },
      { icon: <Plus className="size-4" />, label: 'New Deal' },
    ]
  }
  if (pathname.startsWith('/deals/')) {
    return [
      dashboard,
      { icon: <BriefcaseBusiness className="size-4" />, label: 'Deals', to: '/deals' },
      { label: 'Deal File' },
    ]
  }
  if (pathname === '/tasks') return [dashboard, { icon: <ListChecks className="size-4" />, label: 'Tasks' }]
  if (pathname === '/notifications') return [dashboard, { icon: <Bell className="size-4" />, label: 'Notifications' }]
  if (pathname === '/documents') return [dashboard, { icon: <FileText className="size-4" />, label: 'Documents' }]
  if (pathname.startsWith('/admin/document-layouts')) {
    return [dashboard, { icon: <FileStack className="size-4" />, label: 'Document Layouts' }]
  }
  if (pathname === '/compliance') return [dashboard, { icon: <Scale className="size-4" />, label: 'Compliance' }]
  if (pathname === '/agents') return [dashboard, { icon: <Users className="size-4" />, label: 'Agents' }]

  return [dashboard, { label: getRouteTitle(pathname) }]
}
